function normalizeEgyptPhone(raw) {
  if (!raw) return '';
  const digits = String(raw).replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('20')) return `+${digits}`;
  if (digits.startsWith('0')) return `+20${digits.slice(1)}`;
  return `+20${digits}`;
}

function isTelegramEnabled() {
  return String(process.env.REACT_APP_TELEGRAM_ENABLED || '').toLowerCase() === 'true';
}

function isTelegramDebug() {
  return String(process.env.REACT_APP_TELEGRAM_DEBUG || '').toLowerCase() === 'true';
}

function maskBotToken(token) {
  if (!token) return '';
  const str = String(token);
  if (str.length <= 10) return '***';
  return `${str.slice(0, 4)}***${str.slice(-4)}`;
}

function truncateTelegramText(text, maxLen = 3900) {
  const raw = String(text || '');
  if (raw.length <= maxLen) return raw;
  return `${raw.slice(0, Math.max(0, maxLen - 1))}…`;
}

function getTelegramConfig() {
  const botToken = process.env.REACT_APP_TELEGRAM_BOT_TOKEN;
  const chatId = process.env.REACT_APP_TELEGRAM_CHAT_ID;
  const chatIdsRaw = process.env.REACT_APP_TELEGRAM_CHAT_IDS;
  const chatIds = String(chatIdsRaw || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  return {
    botToken,
    chatId,
    chatIds,
    endpointBase: process.env.REACT_APP_TELEGRAM_ENDPOINT || 'https://api.telegram.org',
  };
}

function normalizeTelegramChatId(raw) {
  const id = String(raw || '').trim();
  if (!id) return '';

  // Supergroups in Telegram API are -100xxxxxxxxxx.
  // Telegram may return migrate_to_chat_id as 100xxxxxxxxxx (without the leading '-').
  if (id.startsWith('100')) return `-${id}`;
  return id;
}

export function buildNewOrderTelegramMessage(order) {
  const shortId = order?.id ? String(order.id).slice(-8) : '';
  const orderId = order?.id ? String(order.id) : '';
  const baseUrl = order?.baseUrl ? String(order.baseUrl) : '';
  const orderLink = order?.orderUrl
    ? String(order.orderUrl)
    : (baseUrl && orderId ? `${baseUrl}/admin/orders/${orderId}` : '');

  const customerName = order?.customerName || order?.name || '';
  const customerPhone = order?.customerPhone || order?.phone || '';
  const address = order?.customerAddress || order?.address || '';
  const total = order?.totalAmount ?? '';
  const paymentMethod = order?.paymentMethod || '';
  const shippingMethod = order?.shippingMethod || '';

  const items = Array.isArray(order?.items) ? order.items : [];
  const itemsList = items
    .map((item) => {
      const qty = item?.quantity || 1;
      const name = item?.name || 'منتج';
      const size = item?.size ? ` (${item.size}${item?.sizeUnit ? ` ${item.sizeUnit}` : ''})` : '';
      return `${qty} × ${name}${size}`;
    })
    .join('، ');

  return [
    '🛒 اوردر جديد - VetCare Store',
    shortId ? `رقم الاوردر: #${shortId}` : null,
    orderLink ? `لينك الاوردر: ${orderLink}` : null,
    customerName ? `الاسم: ${customerName}` : null,
    customerPhone ? `الموبايل: ${normalizeEgyptPhone(customerPhone)}` : null,
    address ? `العنوان: ${address}` : null,
    itemsList ? `الطلبات: ${itemsList}` : null,
    total !== '' ? `الاجمالي: ${total} جنيه` : null,
    paymentMethod ? `طريقة الدفع: ${paymentMethod}` : null,
    shippingMethod ? `طريقة الشحن: ${shippingMethod}` : null,
  ].filter(Boolean).join('\n');
}

export async function notifyNewOrderViaTelegram(order) {
  if (!isTelegramEnabled()) return { ok: false, reason: 'disabled' };

  // If the browser is offline (or DevTools throttling set to Offline), sending will never succeed.
  if (typeof navigator !== 'undefined' && navigator && navigator.onLine === false) {
    return { ok: false, reason: 'offline' };
  }

  const { botToken, chatId, chatIds, endpointBase } = getTelegramConfig();
  if (!botToken) return { ok: false, reason: 'missing_bot_token' };
  const targets = Array.from(
    new Set((chatIds && chatIds.length ? chatIds : [chatId])
      .map(normalizeTelegramChatId)
      .filter(Boolean))
  );
  if (!targets.length) return { ok: false, reason: 'missing_chat_id' };

  const text = truncateTelegramText(buildNewOrderTelegramMessage(order));

  const endpoint = `${endpointBase}/bot${botToken}/sendMessage`;

  // Browser-safe send: use an Image GET request to avoid CORS fetch restrictions.
  const sendToChatId = async (targetChatId) => {
    const url = `${endpoint}` +
      `?chat_id=${encodeURIComponent(targetChatId)}` +
      `&text=${encodeURIComponent(text)}` +
      `&disable_web_page_preview=true`;

    // Fire-and-forget. We can't read Telegram response from the browser reliably.
    try {
      // Keep a reference briefly to reduce the chance of the request being GC'd.
      const pool = (typeof window !== 'undefined')
        ? (window.__tgImgPool = window.__tgImgPool || [])
        : null;

      const img = new Image();
      img.referrerPolicy = 'no-referrer';
      img.src = url;

      if (pool) {
        pool.push(img);
        setTimeout(() => {
          const idx = pool.indexOf(img);
          if (idx >= 0) pool.splice(idx, 1);
        }, 5000);
      }

      if (isTelegramDebug() && typeof console !== 'undefined') {
        console.info('[telegram] sendMessage queued', {
          endpointBase,
          bot: maskBotToken(botToken),
          chatId: String(targetChatId),
          textLength: text.length,
        });
      }
    } catch (_) {
      // ignore
    }
  };

  await Promise.all(targets.map((t) => sendToChatId(t)));
  return { ok: true, targets };
}
